import { useState, useEffect } from 'react';
import { useSiteInfo } from '../context/SiteContext';
import { ArrowUp, MapPin, MessageCircle, Phone, Send } from 'lucide-react';

export default function FloatingButtons() {
  const { siteInfo } = useSiteInfo();
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowTop(window.scrollY > 300);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  const phone = siteInfo.phone?.replace(/\./g, '').replace(/ /g, '') || '';
  const mapUrl = 'https://maps.app.goo.gl/r4SmC4ZYtN9uR3kr5';
  const zaloUrl = `https://zalo.me/${phone}`;
  const messengerUrl = siteInfo.facebookUrl ? `https://m.me/${siteInfo.facebookUrl.split('/').pop()}` : 'https://m.me/';
  const smsUrl = `sms:${phone}`;

  return (
    <>
      {/* ====== DESKTOP: Floating Left Sidebar ====== */}
      <div className="hidden md:flex fixed left-0 top-1/2 -translate-y-1/2 flex-col items-center bg-white shadow-[0_0_15px_rgba(0,0,0,0.2)] rounded-r-2xl py-2 z-50">
        {[
          { 
            href: mapUrl, 
            icon: <img src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEiKXw1Q8mZFo6fY5fIkRQIWYfg8cQqMRa3iC5-wvyw96XB4h4P0QZbyozVIGrq0CNHZ7g0081VlWImqehPhyphenhyphenO_5GRuhIfkRu3cRN4Td0KacxzkcEHTNrit0KDXhQ2PD9N3fHF3W1SArjCE4LlonBh8HKNxxOqxgsrrAHSumMRYF0nF9u7rHFdSFT8GeQ1oS/s1600/Google_Maps_icon_%282020%29.svg.png" className="w-7 h-7 object-contain mx-auto" alt="Map" />, 
            label: 'Tìm đường' 
          },
          { 
            href: zaloUrl, 
            icon: <img src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEg3DrZoAwHqbR-DuIy32rVDU8jhXVN5BI1EFLFgt6TLycc0Ww9n1xenD4_7rMP4_jgdvHbyu_2GuTN2hO7nLrTMCXG0FRtxB8eMRVfep-xD-fBupQ-cbgJhURHIRyb7pruocOhes_T0Ha1pFftDu2wJ3r-DkNlCUELll4-KmsE5uA_5oBKc26GveRQf6x7N/s1600/Icon_of_Zalo.svg.webp" className="w-7 h-7 object-contain mx-auto" alt="Zalo" />, 
            label: 'Chat Zalo' 
          },
          { 
            href: `tel:${phone}`, 
            icon: (
              <div className="w-7 h-7 bg-[#72c212] rounded-full flex items-center justify-center mx-auto text-white shadow-[0_4px_10px_rgba(114,194,18,0.4)] hover:scale-110 transition-transform">
                <Phone size={15} fill="currentColor" />
              </div>
            ), 
            label: 'Gọi điện' 
          },
          { 
            href: messengerUrl, 
            icon: <img src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEhe0gSHksqgU2llRHRyTrUvMZTB4V3-rb_IgfOBzle9DZJWkXY9qsurzlteYut7MzYPXtl-DiuldrSGy1KaeH5M3-xBVa9yLU39_PvDYBtxDblhAKG0SJID6x1zwbvuQWwSp2ouDNO-Wwkg7Q7jEKQHLdjWVJrZHqRLL7b1ZLsMcYhNunMchH6Qnx-aQZyX/s1600/Facebook_Messenger_logo_2020.svg.png" className="w-7 h-7 object-contain mx-auto" alt="Messenger" />, 
            label: 'Messenger' 
          },
        ].map((item, i) => (
          <a
            key={i}
            href={item.href}
            target={item.href.startsWith('tel:') ? '_self' : '_blank'}
            rel="noreferrer"
            className="flex flex-col items-center justify-center p-2 hover:bg-black/5 transition-colors w-[60px] border-b border-gray-100 last:border-0"
          >
            {item.icon}
            <span className="text-[10px] font-semibold mt-1 text-gray-700 whitespace-nowrap">
              {item.label}
            </span>
          </a>
        ))}
      </div>

      {/* ====== DESKTOP: Right-side floating buttons (Zalo + Call + Back to top) ====== */}
      <div className="flex fixed bottom-[calc(80px+env(safe-area-inset-bottom,0px))] right-4 md:bottom-6 md:right-6 flex-col items-end z-50">
        {showTop && (
          <button
            type="button"
            aria-label="Lên đầu trang"
            onClick={scrollToTop}
            className="w-12 h-12 bg-primary hover:bg-primary-dark text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all duration-200 relative group"
          >
            <span className="absolute right-full mr-3 bg-gray-800 text-white px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Lên đầu trang
            </span>
            <ArrowUp size={20} />
          </button>
        )}
      </div>

      {/* ====== MOBILE: Sticky Bottom Bar ====== */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-primary-dark to-primary shadow-2xl rounded-t-2xl">
        <div className="flex items-stretch">
          {[
            { 
              href: mapUrl, 
              icon: <img src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEiKXw1Q8mZFo6fY5fIkRQIWYfg8cQqMRa3iC5-wvyw96XB4h4P0QZbyozVIGrq0CNHZ7g0081VlWImqehPhyphenhyphenO_5GRuhIfkRu3cRN4Td0KacxzkcEHTNrit0KDXhQ2PD9N3fHF3W1SArjCE4LlonBh8HKNxxOqxgsrrAHSumMRYF0nF9u7rHFdSFT8GeQ1oS/s1600/Google_Maps_icon_%282020%29.svg.png" alt="Map" className="w-[22px] h-[22px] object-contain drop-shadow bg-white rounded-full p-0.5" />, 
              label: 'Tìm đường', target: '_blank' 
            },
            { 
              href: zaloUrl, 
              icon: <img src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEg3DrZoAwHqbR-DuIy32rVDU8jhXVN5BI1EFLFgt6TLycc0Ww9n1xenD4_7rMP4_jgdvHbyu_2GuTN2hO7nLrTMCXG0FRtxB8eMRVfep-xD-fBupQ-cbgJhURHIRyb7pruocOhes_T0Ha1pFftDu2wJ3r-DkNlCUELll4-KmsE5uA_5oBKc26GveRQf6x7N/s1600/Icon_of_Zalo.svg.webp" alt="Zalo" className="w-[22px] h-[22px] object-contain drop-shadow bg-white rounded-full p-0.5" />, 
              label: 'Chat Zalo', target: '_blank' 
            },
            { 
              href: `tel:${phone}`, 
              icon: (
                <div className="w-[28px] h-[28px] bg-white text-primary-dark rounded-full flex items-center justify-center -mt-1 shadow-md">
                  <Phone size={16} fill="currentColor" />
                </div>
              ), 
              label: 'Gọi điện', target: '_self', highlight: true 
            },
            { 
              href: messengerUrl, 
              icon: <img src="https://blogger.googleusercontent.com/img/b/R29vZ2xl/AVvXsEhe0gSHksqgU2llRHRyTrUvMZTB4V3-rb_IgfOBzle9DZJWkXY9qsurzlteYut7MzYPXtl-DiuldrSGy1KaeH5M3-xBVa9yLU39_PvDYBtxDblhAKG0SJID6x1zwbvuQWwSp2ouDNO-Wwkg7Q7jEKQHLdjWVJrZHqRLL7b1ZLsMcYhNunMchH6Qnx-aQZyX/s1600/Facebook_Messenger_logo_2020.svg.png" alt="Messenger" className="w-[22px] h-[22px] object-contain drop-shadow bg-white rounded-full p-0.5" />, 
              label: 'Messenger', target: '_blank' 
            },
            { 
              href: smsUrl, 
              icon: <Send size={18} />, 
              label: 'Nhắn SMS', target: '_self' 
            },
          ].map((item, i) => (
            <a
              key={i}
              href={item.href}
              target={item.target}
              rel="noreferrer"
              className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 transition-all active:scale-95 ${
                item.highlight
                  ? 'bg-accent text-primary-dark rounded-t-xl -mt-1 shadow-lg border-x-2 border-orange-300'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              <span className={item.highlight ? 'scale-125' : ''}>{item.icon}</span>
              <span className="text-[10px] font-medium leading-tight text-center">{item.label}</span>
            </a>
          ))}
        </div>
        {/* Safe area spacer for notch phones */}
        <div className="h-safe-area" style={{ height: 'env(safe-area-inset-bottom, 0px)' }} />
      </div>

      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}
